import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Route } from '@app/core/enums/route.enum';
import { MyWorkHistoryItem } from '@app/core/types/work/work.types';
import { BaseContainer } from '@app/shared/abstract/base.container';
import { formatDate, getWeekNumberForOffset, getYearForWeekOffset } from '@app/shared/utils/time.functions';
import { ModalController } from '@ionic/angular';
import { DateTime } from 'luxon';
import { filter, map, Observable } from 'rxjs';
import { WorkSandbox } from '../../sandboxes/work.sandbox';
import { faClock, faMessageQuestion, faPiggyBank, faReceipt } from '@fortawesome/pro-solid-svg-icons';

@Component({
    selector: 'c-worked-hours-and-payments',
    templateUrl: './worked-hours-and-payments.component.html',
    styleUrls: ['./worked-hours-and-payments.component.scss'],
    standalone: false
})
export class WorkedHoursAndPaymentsComponent extends BaseContainer {
  private readonly route = inject(ActivatedRoute);
  currentWeek$: Observable<MyWorkHistoryItem> = this.workSandbox.workHistoryForWeekOfYear$(
    this.currentWeek,
    this.currentYear
  );
  previousWeek$: Observable<MyWorkHistoryItem> = this.workSandbox.workHistoryForWeekOfYear$(
    getWeekNumberForOffset(-1),
    getYearForWeekOffset(-1)
  );
  nextPaymentDate$: Observable<string> = this.workSandbox.nextPaymentDate$.pipe(
    filter((paymentDate) => !!paymentDate),
    map((paymentDate) => formatDate(paymentDate))
  );

  showWorkedHours$ = this.workSandbox.showWorkedHours$;
  showNextPaymentDate$ = this.workSandbox.showNextPaymentDate$;

  timeRegistrationFeatureFlag = this.workSandbox.timeRegistrationFeatureFlag;
  expensesFeatureFlag = this.workSandbox.expensesFeatureFlag;
  requestsFeatureFlag = this.workSandbox.requestsFeatureFlag;
  balanceFeatureFlag = this.workSandbox.balanceFeatureFlag;
  payslipFeatureFlag = this.workSandbox.payslipFeatureFlag;
  invoiceFeatureFlag = this.workSandbox.invoiceFeatureFlag;

  readonly hourRegistrationIcon = faClock;
  readonly expensesIcon = faReceipt;
  readonly requestsIcon = faMessageQuestion;
  readonly balanceIcon = faPiggyBank;

  constructor(
    public workSandbox: WorkSandbox,
    public modalCtrl: ModalController,
    private router: Router
  ) {
    super();
  }

  get currentWeek(): number {
    return getWeekNumberForOffset(0);
  }

  get currentYear(): number {
    return getYearForWeekOffset(0);
  }

  navigateToWeek(historyItem: MyWorkHistoryItem): void {
    this.router.navigate([Route.TimeRegistration], {
      queryParams: { year: historyItem.year, weekNumber: historyItem.weekNumber },
      relativeTo: this.route
    });
  }

  navigateToTimeRegistration(): void {
    this.router.navigate([Route.TimeRegistration], { relativeTo: this.route });
  }

  navigateToExpenses(): void {
    this.router.navigate([Route.Expenses], { relativeTo: this.route, queryParams: { year: DateTime.now().year, month: DateTime.now().month}});
  }

  navigateToBalance(): void {
    this.router.navigate([Route.Balance], { relativeTo: this.route });
  }

  navigateToRequests(): void {
    this.router.navigate([Route.Requests, Route.Holidays], { relativeTo: this.route });
  }

  openLatestInvoice(): void {
    this.workSandbox.openLatestInvoice();
  }

  openLatestPayslip(): void {
    this.workSandbox.openLatestPayslip();
  }
}
